import { useState } from "react";
import { Alert, Box, Button, CircularProgress, Typography, useTheme } from "@mui/material";
import { authClient } from "../../lib/auth-client";
import { isNative } from "../../lib/platform";
import { restorePurchases, showManageSubscriptions } from "../../lib/revenueCat";
import { getNativePlanByRcProductId } from "../../data/pricingPlans";
import { MembershipStatus } from "../../types/lemonSqueezy";
import { useChatQuota } from "../Chat/hooks/useChatQuota";
import { TopUpCreditsDialog } from "../Membership/TopUpCredits";

type NativeManageSubscriptionProps = {
  membership: NonNullable<MembershipStatus["membership"]>;
};

/**
 * Management surface for store-billed (RevenueCat) subscriptions. Plan
 * switches, cancellation, and billing all live in the OS subscription sheet —
 * there is no in-app proration flow like the Lemon Squeezy ManagePlan.
 * Rendered on web too (text-only) when a store subscriber signs in there.
 */
const NativeManageSubscription = ({ membership }: NativeManageSubscriptionProps) => {
  const theme = useTheme();
  const { data: session } = authClient.useSession();
  const { data: quota } = useChatQuota();
  const [busy, setBusy] = useState<"manage" | "restore" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [restored, setRestored] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);

  // Native sells Pro only, so a store product id resolves the TERM, not a tier.
  const nativePlan = getNativePlanByRcProductId(membership.rcProductId);
  const planLabel = nativePlan ? `Insight Pro (${nativePlan.label})` : "Insight Pro";
  const storeLabel = membership.store === "play_store" ? "Google Play" : "the App Store";

  const userId = session?.user?.id;

  const handleManage = async () => {
    if (!userId) return;
    setBusy("manage");
    setError(null);
    try {
      await showManageSubscriptions(userId);
    } catch (err: any) {
      setError(err?.message ?? "Could not open subscription settings");
    } finally {
      setBusy(null);
    }
  };

  const handleRestore = async () => {
    if (!userId) return;
    setBusy("restore");
    setError(null);
    setRestored(false);
    try {
      await restorePurchases(userId);
      setRestored(true);
    } catch (err: any) {
      setError(err?.message ?? "Could not restore purchases");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        mb: 3,
        borderRadius: 3,
        backgroundColor:
          theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.02)",
        border:
          theme.palette.mode === "dark"
            ? "1px solid rgba(255, 255, 255, 0.1)"
            : "1px solid rgba(0, 0, 0, 0.1)",
      }}
    >
      <Typography variant="h3" sx={{ fontWeight: 600, mb: 1 }}>
        Manage plan
      </Typography>
      <Typography variant="body1" sx={{ mb: 0.5 }}>
        Current plan: <strong>{planLabel}</strong>
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
        Your subscription is billed through {storeLabel} and is managed in your device's
        subscription settings.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {restored && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Purchases restored — your access will refresh shortly.
        </Alert>
      )}

      {isNative() && (
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Button
            onClick={handleManage}
            disabled={busy !== null || !userId}
            sx={{
              backgroundColor: "#22c55e",
              color: "white",
              fontWeight: 600,
              textTransform: "none",
              px: 3,
              "&:hover": { backgroundColor: "#16a34a" },
            }}
          >
            {busy === "manage" && <CircularProgress size={20} color="inherit" />}
            {busy !== "manage" && "Manage subscription"}
          </Button>
          <Button
            onClick={handleRestore}
            disabled={busy !== null || !userId}
            variant="outlined"
            sx={{ fontWeight: 600, textTransform: "none", px: 3 }}
          >
            {busy === "restore" && <CircularProgress size={20} color="inherit" />}
            {busy !== "restore" && "Restore purchases"}
          </Button>
        </Box>
      )}

      {/* Credit top-ups are store consumables via RevenueCat — never a Lemon
          Squeezy surface (App Store 3.1.1). Only shown to Pro members. */}
      {isNative() && quota?.credits?.canTopUp === true && (
        <Box sx={{ mt: 3 }}>
          {/* Both usage buckets, side by side — mirrors the web plan tile so
              "0 credits" reads as an empty reserve next to the weekly
              allowance, never as "no usage left". */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
            {[
              {
                label: "Weekly AI Usage",
                value: `${100 - quota.percentUsed}% left`,
                sub: "Resets every Sunday",
              },
              {
                label: "AI Credits",
                value: String(quota.credits.balanceCredits),
                sub:
                  quota.credits.balanceCredits > 0
                    ? "Spent after weekly AI"
                    : "Top up any time",
              },
            ].map((stat) => (
              <Box
                key={stat.label}
                sx={{
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 2,
                  border: `1px solid ${
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.08)"
                      : "rgba(0, 0, 0, 0.08)"
                  }`,
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.02)"
                      : "rgba(0, 0, 0, 0.02)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.625rem",
                    fontWeight: 600,
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                    color: theme.palette.text.secondary,
                  }}
                >
                  {stat.label}
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", mt: 0.25 }}>
                  {stat.value}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    display: "block",
                    mt: 0.25,
                    fontSize: "0.68rem",
                  }}
                >
                  {stat.sub}
                </Typography>
              </Box>
            ))}
          </Box>
          <Button
            onClick={() => setTopUpOpen(true)}
            sx={{
              mt: 1.5,
              backgroundColor: "#22c55e",
              color: "white",
              fontWeight: 600,
              textTransform: "none",
              px: 3,
              "&:hover": { backgroundColor: "#16a34a" },
            }}
          >
            Top up credits
          </Button>
        </Box>
      )}

      {/* Purchased credits from a former Pro plan freeze on Basic — never
          shown as gone, or a downgrade reads as confiscation. Same stat-cell
          language as the Pro grid above, greyed out: a balance waiting, not
          prose. */}
      {isNative() && (quota?.credits?.frozenCredits ?? 0) > 0 && (
        <Box
          sx={{
            mt: 3,
            px: 1.5,
            py: 1.25,
            borderRadius: 2,
            opacity: 0.55,
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.08)"
                : "rgba(0, 0, 0, 0.08)"
            }`,
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.02)"
                : "rgba(0, 0, 0, 0.02)",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.625rem",
              fontWeight: 600,
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              color: theme.palette.text.secondary,
            }}
          >
            AI Credits
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", mt: 0.25 }}>
            {quota?.credits?.frozenCredits}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              display: "block",
              mt: 0.25,
              fontSize: "0.68rem",
            }}
          >
            Paused — ready when you're back on Pro
          </Typography>
        </Box>
      )}
      <TopUpCreditsDialog open={topUpOpen} onClose={() => setTopUpOpen(false)} />
    </Box>
  );
};

export default NativeManageSubscription;
